/**
 * Step 4 — Review and publish (PRD §7.5): grouped read-back with per-section
 * Edit links, the distribution choice with its visibility consequences spelled
 * out, and the confidential-employer option (Tabbio + Company page only).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text, type TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import type { JobEligibilityLocation } from '@tabbio/types';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { useHaptics } from '../../../hooks/useHaptics';
import {
  CITY_OPTIONS,
  COUNTRY_OPTIONS,
  DEPARTMENT_OPTIONS,
  EMPLOYMENT_OPTIONS,
  WORK_TYPE_OPTIONS,
  getOptionLabel,
  getRegionLabelKeyForCountry,
  type OptionItem,
} from '../post-job-flow.data';
import {
  DISTRIBUTION_VALUES,
  assembleDefaultQuestions,
  isBoardDistribution,
} from './post-job-machine';
import {
  FlowTextField,
  InlineError,
  fieldStyles,
} from './post-job-ui';
import { AppSwitch } from '../../common/AppSwitch';
import type { PostJobFlowController } from './use-post-job-flow';

function ReviewSection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit?: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  return (
    <View
      style={[
        styles.section,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
      ]}
    >
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: theme.textTertiary }]}>
          {title}
        </Text>
        {onEdit ? (
          <Pressable onPress={onEdit} hitSlop={8} accessibilityRole="button">
            <Text style={[styles.editLink, { color: colors.primary }]}>
              {t('jobFlow.review.edit')}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {children}
    </View>
  );
}

function ReviewLine({ label, value }: { label: string; value: string }) {
  const { theme } = useTheme();
  if (!value) return null;
  return (
    <View style={styles.line}>
      <Text style={[styles.lineLabel, { color: theme.textTertiary }]}>
        {label}
      </Text>
      <Text style={[styles.lineValue, { color: theme.text }]}>{value}</Text>
    </View>
  );
}

export function PostJobReviewStep({ flow }: { flow: PostJobFlowController }) {
  const { t } = useTranslation('employer');
  const { t: tCommon } = useTranslation('common');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const labelRef = useRef<TextInput>(null);

  const {
    state,
    goToStep,
    setDistribution,
    setConfidential,
    errorFor,
    focusFieldId,
    clearFocusField,
  } = flow;

  useEffect(() => {
    if (focusFieldId === 'confidentialLabel') {
      labelRef.current?.focus();
      clearFocusField();
    }
  }, [clearFocusField, focusFieldId]);

  const optionLabel = (options: OptionItem[], value: string) => {
    const key = getOptionLabel(options, value);
    return key ? (tCommon(key as never) as string) : value;
  };

  const basicsMeta = [
    state.basics.department
      ? optionLabel(DEPARTMENT_OPTIONS, state.basics.department)
      : null,
    state.basics.city ? optionLabel(CITY_OPTIONS, state.basics.city) : null,
    state.basics.country
      ? optionLabel(COUNTRY_OPTIONS, state.basics.country)
      : null,
    optionLabel(WORK_TYPE_OPTIONS, state.basics.workMode),
    optionLabel(EMPLOYMENT_OPTIONS, state.basics.employment),
    t(`jobFlow.options.seniority.${state.basics.seniority}` as never) as string,
  ]
    .filter(Boolean)
    .join(' · ');

  const salaryValue = useMemo(() => {
    const { min, max, currency, period } = state.compensation;
    const hasMin = min.trim().length > 0;
    const hasMax = max.trim().length > 0;
    if (!hasMin && !hasMax) return t('jobFlow.review.salaryNotSet') as string;
    const range =
      hasMin && hasMax ? `${min.trim()}–${max.trim()}` : (min || max).trim();
    const periodLabel = t(
      `jobFlow.options.period.${period}` as never,
    ) as string;
    const visibility = state.compensation.visible
      ? (t('jobFlow.review.salaryShown') as string)
      : (t('jobFlow.review.salaryHidden') as string);
    return `${currency} ${range} · ${periodLabel} · ${visibility}`;
  }, [state.compensation, t]);

  const candidateLocationLine = useCallback(
    (value: JobEligibilityLocation): string | null => {
      const { country, city } = state.basics;
      const countryKey = getOptionLabel(COUNTRY_OPTIONS, country);
      const cityKey = getOptionLabel(CITY_OPTIONS, city);
      const regionKey = getRegionLabelKeyForCountry(country);
      const countryName = countryKey
        ? (tCommon(countryKey as never) as string)
        : country.trim();
      const cityName = cityKey ? (tCommon(cityKey as never) as string) : city.trim();
      const regionName = regionKey ? (tCommon(regionKey as never) as string) : '';

      // The generic fallbacks mirror the picker: a stored tier whose half the
      // job never recorded still reads back, rather than dropping the line and
      // making the review look like the answer was never given.
      switch (value) {
        case 'in_country':
          return countryName
            ? (t('jobFlow.options.candidateLocation.in_country', {
                country: countryName,
              }) as string)
            : (t('jobFlow.options.candidateLocation.in_country_generic') as string);
        case 'in_city':
          return cityName
            ? (t('jobFlow.options.candidateLocation.in_city', {
                city: cityName,
              }) as string)
            : (t('jobFlow.options.candidateLocation.in_city_generic') as string);
        case 'in_region':
          return regionName
            ? (t('jobFlow.options.candidateLocation.in_region', {
                region: regionName,
              }) as string)
            : (t('jobFlow.options.candidateLocation.in_region_generic') as string);
        case 'open_to_relocation':
          return countryName
            ? (t('jobFlow.options.candidateLocation.open_to_relocation', {
                country: countryName,
              }) as string)
            : (t(
                'jobFlow.options.candidateLocation.open_to_relocation_any',
              ) as string);
        default:
          return null;
      }
    },
    [state.basics, t, tCommon],
  );

  const requirementLines = useMemo(() => {
    const lines: string[] = [];
    const { eligibility } = state;
    // Candidate location is country-relative, so its label needs the same
    // country/city/region substitutions the picker made.
    if (eligibility.candidateLocation !== 'any') {
      const line = candidateLocationLine(eligibility.candidateLocation);
      if (line) lines.push(line);
    }
    if (eligibility.experienceYears !== 'any') {
      lines.push(
        t(
          `jobFlow.options.experienceYears.${eligibility.experienceYears}` as never,
        ) as string,
      );
    }
    if (eligibility.language !== 'any') {
      lines.push(
        t(`jobFlow.options.language.${eligibility.language}` as never) as string,
      );
    }
    if (eligibility.workEligibility !== 'any') {
      lines.push(
        t(
          `jobFlow.options.workEligibility.${eligibility.workEligibility}` as never,
        ) as string,
      );
    }
    if (eligibility.workforceProgramme.value !== 'none') {
      lines.push(
        t(
          `jobFlow.options.nationality.${eligibility.workforceProgramme.value}` as never,
        ) as string,
      );
    }
    return lines;
  }, [candidateLocationLine, state, t]);

  const questionCount =
    assembleDefaultQuestions(state.questions.defaults, (key) => key).length +
    state.questions.custom.length;

  // Both board options can mask the employer; "Tabbio + Company page" cannot
  // keep the masking, and toggling it on moves the selection to board-only.
  const canBeConfidential = isBoardDistribution(state.distribution);
  const confidentialActive = canBeConfidential && state.confidential.enabled;
  const publicLabel = state.confidential.publicLabel.trim();

  // One plain sentence for the whole card. The radio and the toggle each state
  // a rule; this states the outcome, which is what the employer is actually
  // deciding and what the old layout left them to infer.
  const audienceSummary = t(
    `jobFlow.distribution.${state.distribution}.summary` as never,
  ) as string;
  const identitySummary = confidentialActive
    ? (t('jobFlow.confidential.summary', {
        label: publicLabel || (t('jobFlow.confidential.unnamedLabel') as string),
      }) as string)
    : null;

  return (
    <View style={styles.wrap}>
      <ReviewSection
        title={t('jobFlow.review.basicsTitle') as string}
        onEdit={() => goToStep('basics')}
      >
        <Text style={[styles.jobTitle, { color: theme.text }]}>
          {state.basics.title || (t('jobFlow.review.untitled') as string)}
        </Text>
        <Text style={[styles.metaLine, { color: theme.textSecondary }]}>
          {basicsMeta}
        </Text>
      </ReviewSection>

      <ReviewSection
        title={t('jobFlow.review.contentTitle') as string}
        onEdit={() => goToStep('requirements')}
      >
        <Text
          style={[styles.summaryPreview, { color: theme.textSecondary }]}
          numberOfLines={3}
        >
          {state.content.summary.trim()}
        </Text>
        <ReviewLine
          label={t('jobFlow.fields.responsibilities') as string}
          value={String(
            state.content.responsibilities.filter((item) => item.trim()).length,
          )}
        />
        <ReviewLine
          label={t('jobFlow.fields.requirements') as string}
          value={String(
            state.content.requirements.filter((item) => item.trim()).length,
          )}
        />
        {state.content.niceToHave.some((item) => item.trim()) ? (
          <ReviewLine
            label={t('jobFlow.fields.niceToHave') as string}
            value={String(
              state.content.niceToHave.filter((item) => item.trim()).length,
            )}
          />
        ) : null}
        {state.content.benefits.some((item) => item.trim()) ? (
          <ReviewLine
            label={t('jobFlow.fields.benefits') as string}
            value={String(
              state.content.benefits.filter((item) => item.trim()).length,
            )}
          />
        ) : null}
      </ReviewSection>

      <ReviewSection
        title={t('jobFlow.review.applicationsTitle') as string}
        onEdit={() => goToStep('applications')}
      >
        <ReviewLine
          label={t('jobFlow.salary.title') as string}
          value={salaryValue}
        />
        <ReviewLine
          label={t('jobFlow.requirementsGroup.title') as string}
          value={
            requirementLines.length > 0
              ? requirementLines.join(' · ')
              : (t('jobFlow.review.noRequirements') as string)
          }
        />
        <ReviewLine
          label={t('jobFlow.review.questionsLabel') as string}
          value={t('jobFlow.review.questionCount', {
            count: questionCount,
          })}
        />
      </ReviewSection>

      {/* Distribution (§7.5 visibility table) */}
      <View style={fieldStyles.group}>
        <Text style={[fieldStyles.label, { color: theme.textTertiary }]}>
          {t('jobFlow.review.distributionTitle')}
        </Text>
        <View style={styles.distributionList}>
          {DISTRIBUTION_VALUES.map((value) => {
            const active = state.distribution === value;
            return (
              <Pressable
                key={value}
                onPress={() => {
                  haptics.trigger('selection');
                  setDistribution(value);
                }}
                accessibilityRole="radio"
                accessibilityState={{ selected: active }}
                style={[
                  styles.distributionOption,
                  {
                    borderColor: active ? colors.primary : theme.borderSubtle,
                    backgroundColor: theme.surface,
                  },
                ]}
              >
                <View
                  style={[
                    styles.radio,
                    {
                      borderColor: active ? colors.primary : theme.border,
                      backgroundColor: active ? colors.primary : 'transparent',
                    },
                  ]}
                >
                  {active ? <View style={styles.radioDot} /> : null}
                </View>
                <View style={styles.distributionTextWrap}>
                  <Text style={[styles.distributionTitle, { color: theme.text }]}>
                    {t(`jobFlow.distribution.${value}.title` as never) as string}
                  </Text>
                  <Text
                    style={[
                      styles.distributionBody,
                      { color: theme.textTertiary },
                    ]}
                  >
                    {t(`jobFlow.distribution.${value}.body` as never) as string}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
        <InlineError messageKey={errorFor('distribution')} />
        <Text style={[styles.audienceSummary, { color: theme.textSecondary }]}>
          {audienceSummary}
          {identitySummary ? ` ${identitySummary}` : ''}
        </Text>
      </View>

      {/* Confidential employer — board distributions only (§7.5) */}
      {canBeConfidential ? (
        <View
          style={[
            styles.section,
            { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
          ]}
        >
          <View style={fieldStyles.switchRow}>
            <View style={fieldStyles.switchTextWrap}>
              <Text style={[fieldStyles.switchTitle, { color: theme.text }]}>
                {t('jobFlow.confidential.toggle')}
              </Text>
              <Text
                style={[fieldStyles.switchSubtitle, { color: theme.textTertiary }]}
              >
                {t('jobFlow.confidential.hint')}
              </Text>
            </View>
            <AppSwitch
              value={state.confidential.enabled}
              onValueChange={(enabled) => setConfidential({ enabled })}
              accessibilityLabel={t('jobFlow.confidential.toggle') as string}
            />
          </View>

          {state.confidential.enabled ? (
            <FlowTextField
              ref={labelRef}
              label={t('jobFlow.confidential.labelField') as string}
              value={state.confidential.publicLabel}
              onChangeText={(publicLabel) => setConfidential({ publicLabel })}
              placeholder={t('jobFlow.confidential.labelPlaceholder') as string}
              errorKey={errorFor('confidentialLabel')}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[4],
  },
  section: {
    borderRadius: borderRadius['2xl'],
    borderWidth: 1,
    padding: spacing[4],
    gap: spacing[2],
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[2],
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  editLink: {
    fontSize: 13,
    fontWeight: '600',
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.2,
    textAlign: 'auto',
    writingDirection: 'auto',
  },
  metaLine: {
    fontSize: 13,
    lineHeight: 19,
  },
  summaryPreview: {
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'auto',
    writingDirection: 'auto',
  },
  line: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing[3],
  },
  lineLabel: {
    fontSize: 13,
    lineHeight: 19,
  },
  lineValue: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '500',
    // Logical end-alignment (rule 7: never hardcode left/right).
    textAlign: 'auto',
    writingDirection: 'auto',
  },
  distributionList: {
    gap: spacing[2],
  },
  distributionOption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[3],
    borderWidth: 1.5,
    borderRadius: borderRadius.xl,
    padding: spacing[4],
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  radioDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#FFFFFF',
  },
  distributionTextWrap: {
    flex: 1,
    gap: 2,
  },
  distributionTitle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 19,
  },
  distributionBody: {
    fontSize: 12,
    lineHeight: 17,
  },
  audienceSummary: {
    fontSize: 13,
    lineHeight: 19,
    marginTop: spacing[2],
  },
});
